'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Product } from 'src/model/Product';
import { StoreProfileUI } from './StoreProfile/ui';
import { api } from 'src/services/api';
import type { JSX } from 'react/jsx-runtime';

// Interfaces para tipar a resposta da API
interface StoreData {
  id: string;
  name: string;
  email: string;
  phone: string;
  description: string;
  imageDetail: string;
  imageBanner: string;
  address: string | null;
  user: any | null;
}

interface ApiResponse {
  store: StoreData;
  productDTOs: Product[];
}

// Função padrão para formatação de preço
const defaultValuePrice = (product: Product, discount = false): string => {
  try {
    // Assumindo que o produto tem uma estrutura de preço similar ao JSON fornecido
    if (product.grade && product.grade.length > 0) {
      const firstGrade = product.grade[0];
      if (firstGrade.gradeSizes && firstGrade.gradeSizes.length > 0) {
        const firstSize = firstGrade.gradeSizes[0];
        if (firstSize.price) {
          const price = firstSize.price.price || 0;
          const discountValue = firstSize.price.discount || 0;

          if (discount && discountValue > 0) {
            const discountedPrice = price - (price * discountValue) / 100;
            return `R$ ${discountedPrice.toFixed(2).replace('.', ',')}`;
          }

          return `R$ ${price.toFixed(2).replace('.', ',')}`;
        }
      }
    }

    // Fallback se não encontrar preço
    return 'Preço não disponível';
  } catch (error) {
    console.error('Erro ao formatar preço:', error);
    return 'Preço não disponível';
  }
};

export const StoreProfileScreen: React.FC<{
  valuePrice?: (product: Product, discount: boolean) => string;
}> = ({ valuePrice }): JSX.Element => {
  const { id: storeIdFromUrl } = useParams<{ id?: string }>();
  const location = useLocation();

  const [imageDetail, setImageDetail] = useState<string>('');
  const [imageBanner, setImageBanner] = useState<string>('');
  const [storeName, setStoreName] = useState<string>('');
  const [productCount, setProductCount] = useState<number>(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [storeDescription, setStoreDescription] = useState<string>('');
  const [storeEmail, setStoreEmail] = useState<string>('');
  const [storePhone, setStorePhone] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [userCity, setUserCity] = useState<string | undefined>(undefined); // ADICIONAR

  // Garantir que sempre temos uma função válida para valuePrice
  const safeValuePrice =
    typeof valuePrice === 'function' ? valuePrice : defaultValuePrice;

  useEffect(() => {
    const fetchData = async () => {
      try {
        let apiResponse: ApiResponse;

        if (storeIdFromUrl) {
          // Para uma loja específica
          const response = await api.get(`/product/store/${storeIdFromUrl}`);
          apiResponse = response.data;
        } else if (location.pathname === '/store-profile') {
          console.log('Entrou storeprofile');

          // Para a loja do usuário logado
          const storeResponse = await api.get(`/store/find-store/`);
          const productResponse = await api.get(`/product/find-store/`);

          // Se as APIs separadas retornam dados diferentes, construa o objeto
          apiResponse = {
            store: storeResponse.data,
            productDTOs: Array.isArray(productResponse.data)
              ? productResponse.data
              : [],
          };
        } else {
          throw new Error('Rota desconhecida');
        }

        console.log('Resposta completa da API:', apiResponse);

        // Extrair dados da loja
        const storeData = apiResponse.store;
        // Extrair produtos (garantindo que seja um array)
        const productData = Array.isArray(apiResponse.productDTOs)
          ? apiResponse.productDTOs
          : [];

        console.log('Dados da loja:', storeData);
        console.log('Produtos:', productData);

        setProducts(productData);
        setProductCount(productData.length);
        setStoreName(storeData?.name || 'Loja Desconhecida');
        setStoreDescription(storeData?.description || 'Sem descrição');
        setStoreEmail(storeData?.email || 'Sem e-mail');
        setStorePhone(storeData?.phone || 'Sem telefone');
        setImageDetail(storeData?.imageDetail || '');
        setImageBanner(storeData?.imageBanner || '');

        try {
          const userResponse = await api.get('/user/findeone');
          const userData = userResponse.data;
          const city =
            userData?.address?.find((addr: any) => addr.main === true)?.city ??
            userData?.address?.[0]?.city;
          setUserCity(city);
        } catch (userError) {
          console.log('Usuário não logado ou erro ao buscar dados:', userError);
          setUserCity(undefined);
        }
      } catch (error) {
        console.error('Erro ao carregar dados da loja:', error);
        setError('Erro ao carregar dados');
        // Garantir que products seja um array mesmo em caso de erro
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [storeIdFromUrl, location.pathname]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <StoreProfileUI
      storeName={storeName}
      productCount={productCount}
      products={products}
      valuePrice={safeValuePrice}
      storeDescription={storeDescription}
      storeEmail={storeEmail}
      storePhone={storePhone}
      imageDetail={imageDetail}
      imageBanner={imageBanner}
      storeImage={''}
      userCity={userCity}
    />
  );
};
